import React, { useRef, useEffect } from "react";
import { Grid, styled } from "@mui/material";

// Redux
import { ICrewPairingPositionMap, CrewPairingPositionGrouped } from "src/redux/reducers/CrewScheduleReducer";
import { defaultCrewRowSettings } from "src/pages/CrewSchedule/TimelineUtils";
import { isIFullPairingPosition } from "@vmosolutions/oc-interfaces";
import { CrewPairingPosition } from "./CrewPairingPosition";
import { CrewMemberDropTarget } from "./CrewMemberDropTarget";
interface ICrewPairingListProps {
  id: string;
  crewMembersOnPairing: ICrewPairingPositionMap;
  setCrewListRef: (ref: React.MutableRefObject<HTMLDivElement>) => void;
  disableMouseEvents?: boolean;
  openFlyingDisplayIndex?: number;
}

const OCCrewPairingList = styled(Grid)(({ theme }) => ({
  "@keyframes gradientsweep": {
    "0%": { backgroundPosition: "0% 50%" },
    "50%": { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0% 50%" },
  },
  "@keyframes gradientsweepStop": {
    "0%": { backgroundPosition: "0% 50%" },
    "50%": { backgroundPosition: "50% 50%" },
    "100%": { backgroundPosition: "50% 50%" },
  },
  display: "flex",
  width: "100%",
  flexGrow: 1,
  border: 2,
  flexDirection: "column",
  overflow: "hidden",
  flexBasis: 0,
  "& .crew_details": {
    display: "flex",
    height: "100%",
    fontFamily: "Heebo, sans-serif",
    borderRight: "4px solid " + theme.custom.timeline.groupColumnDivider,
    transition: theme.transitions.create("border", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
    "& .expand_crew_row_btn": {
      display: "none",
      padding: 0,
      borderRadius: 3,
      "& svg": { width: 20, height: 20 },
    },
    "& div.crew_member": {
      display: "flex",
      flexGrow: 1,
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-end",
      padding: "0px 5px 0px 0px",
      width: 85,
      height: defaultCrewRowSettings.crewRowHeight,
      userSelect: "none",
      "&.empty": { padding: 0 },
      "& .crew_firstname": {
        color: theme.custom.timeline.aircraftFleetColor,
        fontSize: 12,
        textAlign: "right",
        transition: theme.transitions.create("color", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shorter,
        }),
      },
      "& .crew_lastname": {
        color: theme.custom.timeline.aircraftRegColor,
        fontSize: 14,
        textAlign: "right",
      },
      "& .crew_identity, & .drop_crew": {
        color: theme.custom.timeline.aircraftTypeColor,
        fontSize: 9,
        lineHeight: "12px",
        textAlign: "right",
      },
      "& .crew_base": {
        color: theme.custom.timeline.aircraftTypeColor,
        fontSize: 12,
        lineHeight: "12px",
        textAlign: "left",
      },
      "& .crew_planned_credit": {
        fontWeight: "bold",
        color: theme.custom.timeline.aircraftRegColor,
        fontSize: 12,
        lineHeight: "12px",
        textAlign: "left",
      },
      "& .drop_crew": {
        border: `1px dashed ${theme.custom.timeline.aircraftRegColor}`,
        opacity: 0.5,
        height: "calc(100% - 10px)",
        margin: 5,
        padding: 5,
        textAlign: "center",
      },
    },
    "&.selected": {
      backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.custom.timeline.contextListBackground})`,
      borderColor: theme.custom.timeline.groupSelected,
      "& div.crew_member": {
        "& .crew_firstname": {
          color: theme.custom.timeline.groupSelected,
        },
        "& .crew_lastname": {
          color: theme.custom.timeline.aircraftTypeColor,
        },
      },
    },
    // When used in the pairing editor, interactions are disables and so should styles.
    "&.presentation:": { userSelect: "none" },
    "&:hover:not(.presentation)": {
      backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.custom.timeline.contextListBackground})`,
      "& .expand_crew_row_btn": { display: "flex" },
    },
    "&.over": {
      "& div.crew_member": {
        "& .crew_identity, & .drop_crew": {
          color: theme.custom.forms.dirtyColor,
        },
        "& .drop_crew": {
          border: `1px dashed ${theme.custom.forms.dirtyColor}`,
          color: theme.custom.forms.dirtyColor,
        },
      },
    },
    /* used to display animated background effect when actively scanning a crew member */
    "&.scan_active": {
      background: `linear-gradient(90deg, ${theme.palette.background.paper}, ${theme.palette.background.paper}, ${theme.palette.grey[600]}, ${theme.palette.background.paper}, ${theme.palette.background.paper})`,
      backgroundSize: "400% 400%",
      animation: "5s ease infinite",
      animationName: "$gradientsweep",
      "&:hover:not(.presentation)": {
        backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.palette.grey[600]})`,
      },
    },
    "&.scan_success": {
      background: `linear-gradient(90deg, ${theme.palette.background.paper}, ${theme.palette.background.paper}, ${theme.palette.success.dark}, ${theme.palette.background.paper}, ${theme.palette.background.paper})`,
      backgroundSize: "400% 400%",
      animation: "5s ease once",
      animationName: "$gradientsweepStop",
      "&:hover:not(.presentation)": {
        backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.palette.success.dark})`,
      },
    },
    "&.scan_errors": {
      background: `linear-gradient(90deg, ${theme.palette.background.paper}, ${theme.palette.background.paper}, ${theme.palette.error.dark}, ${theme.palette.background.paper}, ${theme.palette.background.paper})`,
      backgroundSize: "400% 400%",
      animation: "5s ease once forwards", // "forwards" will cause the animation to stop on the last frame ensuring that the error state remains visible always!
      animationName: "$gradientsweepStop",
      "&:hover:not(.presentation)": {
        backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.palette.error.dark})`,
      },
    },
  },
}));

export const CrewPairingList = (props: ICrewPairingListProps): JSX.Element => {
  const { crewMembersOnPairing, setCrewListRef, disableMouseEvents, openFlyingDisplayIndex, id } = props;
  const crewListView = useRef<any>();

  useEffect(() => {
    setCrewListRef(crewListView);
  }, [setCrewListRef]);

  return (
    <OCCrewPairingList
      container
      direction="column"
      ref={crewListView}
      style={{
        display: "table", // Fixes an issue where height doesn't render correctly.
        height: "100%",
        overflow: "hidden",
        position: "relative",
        flexWrap: "nowrap",
      }}
    >
      {Object.keys(crewMembersOnPairing).map((crewPositionStr: string, blahIndex: number) => {
        const crewMembersOnPosition = crewMembersOnPairing[crewPositionStr];
        return (
          <div key={blahIndex}>
            {crewMembersOnPosition.map((crewMember: CrewPairingPositionGrouped) => {
              const posIndex = crewMember.positionIndex;
              return isIFullPairingPosition(crewMember) ? (
                <CrewPairingPosition
                  windowId={id}
                  pairingPosition={crewMember}
                  index={posIndex}
                  key={`${crewPositionStr}-${blahIndex}-${posIndex}`}
                  openFlyingDisplayIndex={openFlyingDisplayIndex}
                />
              ) : (
                <CrewMemberDropTarget
                  crewMember={crewMember}
                  positionStr={crewPositionStr}
                  key={`${crewPositionStr}-${blahIndex}-${posIndex}`}
                  positionIndex={posIndex}
                  isOnPairing={true}
                  disableMouseEvents={disableMouseEvents}
                  openFlyingDisplayIndex={openFlyingDisplayIndex}
                  windowId={id}
                />
              );
            })}
          </div>
        );
      })}
    </OCCrewPairingList>
  );
};
